import { createContext, ReactNode, useEffect, useReducer } from 'react';
import { ActionMap, AuthState, AuthUser, BxContextType } from '../@types/auth';
import bxApi from "../utils/bxApi";

// ----------------------------------------------------------------------

enum Types {
    Initial = 'INITIALIZE',
    Login = 'LOGIN',
    Logout = 'LOGOUT',
    Register = 'REGISTER',
}

type BxAuthPayload = {
    [Types.Initial]: {
        isAuthenticated: boolean;
        user: AuthUser;
    };
    [Types.Login]: {
        user: AuthUser;
    };
    [Types.Logout]: undefined;
    [Types.Register]: {
        user: AuthUser;
    };
};

export type BxActions = ActionMap<BxAuthPayload>[keyof ActionMap<BxAuthPayload>];

const initialState: AuthState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null,
    companyCode: null
};

const setSession = (accessToken: string | null) => {
    if (accessToken) {
        localStorage.setItem('bxwmcreds', accessToken);
        const parsedToken = JSON.parse(accessToken);
        localStorage.setItem('bxwmcc', parsedToken.companycode);
    } else {
        localStorage.removeItem('bxwmcreds');
        localStorage.removeItem('bxwmcc');
    }
};

const BxReducer = (state: AuthState, action: BxActions) => {
    switch (action.type) {
        case 'INITIALIZE':
            return {
                isAuthenticated: action.payload.isAuthenticated,
                isInitialized: true,
                user: action.payload.user,
                companyCode: action.payload.user?.companycode
            };
        case 'LOGIN':
            return {
                ...state,
                isAuthenticated: true,
                user: action.payload.user,
                companyCode: action.payload.user?.companycode
            };
        case 'LOGOUT':
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                companyCode: null
            };

        default:
            return state;
    }
};

const AuthContext = createContext<BxContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
    children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
    const [state, dispatch] = useReducer(BxReducer, initialState);

    useEffect(() => {
        const initialize = async () => {
            try {
                const companyCode = parseInt(localStorage.getItem('bxwmcc') || '', 10);
                if (companyCode) {
                    try {
                        const { data: sessionResponse } = await bxApi.get(`/${companyCode}/1.0/session`);
                        if (sessionResponse.success) {
                            dispatch({
                                type: Types.Initial,
                                payload: {
                                    isAuthenticated: true,
                                    user: { ...sessionResponse.data, companycode: companyCode }
                                },
                            });
                            return;
                        }
                    } catch (e) {
                        //nothing fallbacking to bxwmcreds
                    }
                }

                const credsStr = localStorage.getItem('bxwmcreds');

                if (credsStr) {
                    try {
                        const creds = JSON.parse(credsStr);
                        const response = await bxApi.post('/1.0/login', {
                            companycode: creds.companycode,
                            username: creds.username,
                            password: creds.password,
                        });
                        const { success } = response.data;
                        if (success) {
                            setSession(JSON.stringify({
                                companycode: creds.companycode,
                                username: creds.username,
                                password: creds.password
                            }));
                            dispatch({
                                type: Types.Initial,
                                payload: {
                                    isAuthenticated: true,
                                    user: response.data.data[0],
                                },
                            });
                            return
                        } else {
                            setSession(null);
                        }

                    } catch (e) {
                        setSession(null);
                    }
                }

                dispatch({
                    type: Types.Initial,
                    payload: {
                        isAuthenticated: false,
                        user: null,
                    },
                });


            } catch (err) {
                setSession(null);
                dispatch({
                    type: Types.Initial,
                    payload: {
                        isAuthenticated: false,
                        user: null,
                    },
                });
            }
        };

        initialize();
    }, []);

    const login = async (companyCode: number | string, username: string, password: string) => {
        const response = await bxApi.post('/1.0/login', {
            companycode: companyCode,
            username,
            password,
        });
        const { success } = response.data;
        if (success) {
            setSession(JSON.stringify({
                companycode: companyCode,
                username: username,
                password: password
            }));
            dispatch({
                type: Types.Login,
                payload: {
                    user: response.data.data[0],
                },
            });

        } else {
            dispatch({
                type: Types.Initial,
                payload: {
                    isAuthenticated: false,
                    user: null,
                },
            });
        }
    };

    const logout = async () => {
        setSession(null);
        dispatch({ type: Types.Logout });
    };

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'bx',
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export { AuthContext, AuthProvider };
